import { backendAPI, blockchainAPI } from "../api"
import { triggerAction } from "../constants"
import { patchEncryptedCacheItem, safeApiCall } from "../api/offline"
import {
  addToCache,
  checkOnlineStatus,
  db,
  decryptObject,
  deleteCacheByKey,
  getCacheByKey,
  resetRequestsErrors,
  updateDependencyRecords,
} from "../../view/shared/utilities/cache"
import { GROUP_ADDED_LOCALLY, GROUP_LOADED_FROM_DB } from "../reducers/base/group"
import { fetchVslasMembers, LOAD_LOCAL_VSLA_MEMBERS } from "./members/getVslaMembers"
import { fetchPreviousMeetingsData } from "./meetings"
import { BLOCKCHAIN_DATA_LOADED } from "../reducers/vslaDataReducer"
import {
  EXPENSE_TRANSACTION,
  getBalances,
  hydrateOfflineBalance,
  INCOME_TRANSACTION,
} from "../reducers/transactions/actions"
import { fetchMeeting } from "./meetings/getMeeting"
import { findMemberName } from "./helpers"
import { canCashout } from "../reducers/cashout/actions"
import { processOfflineQueue, resetQueue } from "../api/queue"
import { MEETING_LOADED_FROM_DB } from "../reducers/base/meeting"

export const vslasLoadingStarted = (bool) => ({
  type: "VSLAS_LOADING_STARTED",
  isLoading: bool,
})

export const vslaLoaded = (vslas) => ({
  type: "VSLAS_LOADED",
  vslas,
})

export const vslaLoadingError = (error) => ({
  type: "VSLA_LOADING_ERROR",
  error,
})

export const loadVslaStart = (bool) => ({
  type: "LOAD_VSLA_START",
  isloading: bool,
})

export const loadVslaSuccess = (vsla) => ({
  type: "LOADED_VSLA_SUCCESS",
  vsla,
})

export const existingGroup = (state) => state.group
export const vslaMetadata = (state) => state.vsla?.metadata
export const members = (state) => state.loadedvslaMembers
export const numberOfMeetings = (state) => state.groupMeetings

export function fetchVslas() {
  return async function (dispatch, getState) {
    const user = getState().user
    if (!user || !user.id) {
      console.error("User not found or user ID is missing.")
      return Promise.reject(new Error("User not found or user ID is missing."))
    }
    const url = `/groups-hedera/${user.id}/groups`
    try {
      const { data } = await safeApiCall(
        {
          cacheEnabled: true,
          url,
        },
        async () => backendAPI.get(url),
      )

      if (Array.isArray(data)) {
        dispatch(triggerAction(GROUP_LOADED_FROM_DB, data[0]))
        dispatch(fetchVsla())
        dispatch(fetchVslasMembers())
        dispatch(fetchPreviousMeetingsData())
        dispatch(getBalances())
        dispatch(fetchMeeting())
        dispatch(canCashout())
      } else {
        // hydrate
        dispatch(loadLocalData())
      }
    } catch (error) {
      dispatch(vslaLoadingError(error))
    }
  }
}

export function fetchVsla() {
  return async function (dispatch, getState) {
    try {
      const { group, user, loadedvslaMembers } = getState()
      const currentUserId = user?.id
      if (!group?.id) {
        console.error("No wallet ID found")
        return
      }

      const secretary = loadedvslaMembers.find((m) => m.id == group.secretary)
      if (secretary) {
        await deleteCacheByKey("offlineRequests", "tempId", secretary?.pubkey)
      }
      await resetRequestsErrors(currentUserId)
      // const url = `/vsla/${blockchain_wallet_id}/author`
      // const { data } = await safeApiCall(
      //   {
      //     cacheEnabled: true,
      //     url: url,
      //   },
      //   async () => blockchainAPI.get(url),
      // )

      const metaObject = {
        userId: currentUserId,
      }

      const requests = await db.offlineRequests.toArray()
      const requireGroupId = requests.filter(({ dependsOn, userId }) => {
        return userId === metaObject.userId && dependsOn === "group"
      })

      let groupIdDependecy = null

      if (requireGroupId.length > 0) {
        groupIdDependecy = await updateDependencyRecords(
          ["group", metaObject.userId],
          {
            ...metaObject,
            key: "group_id",
            value: group?.id,
            entityType: "group",
          },
          metaObject.userId,
        )
      }

      // dispatch(triggerAction(BLOCKCHAIN_DATA_LOADED, data))
      if (groupIdDependecy) {
        // A dependency been resolved. start processing
        resetQueue()
        processOfflineQueue()
      }
      return Promise.resolve()
    } catch (error) {
      console.log("Error in fetchVsla:", error) // Log the error here
      return Promise.reject()
    }
  }
}

/**
 * Loads all locally stored data from IndexedDB and hydrates the Redux store.
 * This should be called on application startup after user authentication.
 */
export function loadLocalData() {
  return async function (dispatch, getState) {
    const { user, group, loadedvslaMembers, meeting, cycles } = getState()
    const activeCycle = cycles?.data?.find((c) => c.isActive)
    const currentUserId = user?.id
    if (!currentUserId) {
      console.log("[Rehydration] No user logged in. Skipping data load.")
      return
    }
    try {
      console.log(`[Rehydration] Loading local group for user ${currentUserId}...`)
      const localGroup = await getCacheByKey("groups", "userId", currentUserId)
      const onlineMeeting = meeting?.ongoing
      const offlineRequests = await db.offlineRequests.toArray()
      const localMembers = offlineRequests.filter(
        ({ userId, entityType }) =>
          userId === currentUserId && entityType === "members",
      )
      const meetings = offlineRequests.filter(
        ({ userId, entityType }) =>
          userId === currentUserId && entityType === "meeting",
      )
      const ongoingMeetingTX = offlineRequests.filter(
        ({ userId, entityType }) =>
          userId === currentUserId &&
          (INCOME_TRANSACTION.includes(entityType) ||
            EXPENSE_TRANSACTION.includes(entityType)),
      )

      if (meetings.length) {
        console.log(
          `[Rehydration] Found ${meetings.length} meetings for user ${currentUserId}.`,
        )
      }
      const closedMeetings = []

      for (const meeting of meetings) {
        let decryptedBody
        try {
          const decryptedString = decryptObject(meeting.encryptedBody, meeting.salt)
          decryptedBody = JSON.parse(decryptedString)
        } catch (e) {
          console.error(
            `[Rehydration] Failed to decrypt meeting ${meeting.cacheId}. Marking as failed.`,
            e,
          )
          await db
            .table("offlineRequests")
            .update(meeting.cacheId, { status: "decryption_failed" })
          continue // Skip to the next meeting
        }
        let meetingData = decryptedBody.body
        const transactions = []
        let txData = ongoingMeetingTX.filter(
          ({ needBodyUpdate }) =>
            needBodyUpdate.meeting_id === meetingData.request_id,
        )
        for (const tx of txData) {
          const decryptedString = decryptObject(tx.encryptedBody, tx.salt)
          const decryptedBody = JSON.parse(decryptedString)
          const txPayload = decryptedBody.body?.data
          const memberPubkey = loadedvslaMembers.find(
            (m) => m.id === txPayload?.member_id ||
              decryptedBody?.needBodyUpdate?.member_id === m.pubkey,
          )?.pubkey
          transactions.push({
            ...txPayload,
            transactionPartyName: findMemberName(
              memberPubkey,
              loadedvslaMembers,
              localMembers,
              localGroup?.secretary,
            ),
            isOffline: true,
            status: "pending",
          })
        }

        if (meetingData?.status === "ongoing") {
          console.log("[Rehydration] Ongoing meeting transactions hydrating...")
          dispatch(
            triggerAction(MEETING_LOADED_FROM_DB, {
              ...meetingData,
              cacheId: meeting.cacheId,
              syncStatus: meeting.status,
              error: meeting.error || null,
            }),
          )
          dispatch(triggerAction("SET_ACTIVE_MEETING_TRANSACTIONS", transactions))
        } else {
          // For closed meetings, we can store them in history
          closedMeetings.push({
            ...meetingData,
            cycle_id: activeCycle?.id || meetingData.cycle_id,
            is_offline: true,
            cacheId: meeting.cacheId,
            syncStatus: meeting.status,
            error: meeting.error || null,
            transactions,
          })
        }
      }

      if (closedMeetings.length) {
        dispatch(triggerAction("SET_PREVIOUS_MEETING_DATA", closedMeetings))
      }

      if (onlineMeeting) {
        let txData = ongoingMeetingTX.filter(
          ({ body }) => body.data.meeting_id === onlineMeeting.id,
        )
        const transactions = onlineMeeting.transactions || []
        for (const tx of txData) {
          const decryptedString = decryptObject(tx.encryptedBody, tx.salt)
          const decryptedBody = JSON.parse(decryptedString)
          const txPayload = decryptedBody.body?.data
          const memberPubkey = loadedvslaMembers.find(
            (m) => m.id === txPayload?.member_id ||
              decryptedBody?.needBodyUpdate?.member_id === m.pubkey,
          )?.pubkey
          transactions.unshift({
            ...txPayload,
            transactionPartyName: findMemberName(
              memberPubkey,
              loadedvslaMembers,
              localMembers,
              localGroup?.secretary,
            ),
            isOffline: true,
            status: "pending",
          })
        }
        dispatch(triggerAction("SET_ACTIVE_MEETING_TRANSACTIONS", transactions))
      }

      const metaObject = { userId: currentUserId }
      const requests = await db.offlineRequests.toArray()
      const requireGroupId = requests.filter(({ dependsOn, userId }) => {
        return userId === metaObject.userId && dependsOn === "group"
      })

      if (requireGroupId.length > 0) {
        await updateDependencyRecords(
          ["group", metaObject.userId],
          {
            ...metaObject,
            key: "group_id",
            value: group?.id,
            entityType: "group",
          },
          metaObject.userId,
        )
      }

      if (localGroup) {
        const { group, blockChain, secretary, accounts } = localGroup
        dispatch(
          triggerAction(GROUP_ADDED_LOCALLY, {
            ...group,
            cacheId: localGroup.cacheId,
            funds: accounts.map((fund) => ({ type: fund, balances: 0 })),
          }),
        )
        dispatch(
          triggerAction(BLOCKCHAIN_DATA_LOADED, {
            ...blockChain,
            accounts: accounts.map((fund) => ({ type: fund, balances: 0 })),
          }),
        )
        // if group is offline load secretary as part of members
        dispatch(
          triggerAction(LOAD_LOCAL_VSLA_MEMBERS, [
            {
              ...secretary,
              syncStatus: "pending",
              error: null,
              cacheId: localGroup.cacheId,
            },
            ...localMembers.map((m) => ({
              ...m.payload.data,
              cacheId: m.cacheId,
              syncStatus: m.status,
              error: m.error || null,
            })),
          ]),
        )
        console.log(
          `[Rehydration] Hydrated Redux with group (cacheId: ${localGroup.cacheId}) for user ${currentUserId}.`,
        )
      } else if (localMembers.length) {
        console.log(
          `[Rehydration] Loading local members for user ${currentUserId}...`,
        )
        dispatch(
          triggerAction(
            LOAD_LOCAL_VSLA_MEMBERS,
            localMembers.map((m) => ({
              ...m.payload.data,
              cacheId: m?.cacheId,
              syncStatus: m?.status,
              error: m.error || null,
            })),
          ),
        )
      }
      checkOnlineStatus().then((online) => {
        // wait for the data to be hydrated first
        if (online) {
          processOfflineQueue()
        }
      })
      dispatch(hydrateOfflineBalance())
      // You can extend this to load other user-specific data as well
    } catch (error) {
      console.error("[Rehydration] Failed to load local data:", error)
    }
  }
}
